import * as React from "react";

import {
  ApolloClient,
  NormalizedCacheObject,
  useLazyQuery,
  useMutation,
} from "@apollo/client";

import {
  GET_REGISTRATION_STATUS_QUERY,
  GET_VIEWINGS_QUERY,
  GET_VIEWING_QUERY,
} from "../graphql/queries";

import {
  RESERVATION_MUTATION,
  SAMTYKKER_MUTATION,
  VALUATION_MUTATION,
} from "../graphql/mutations";

import { validatePhone } from "../validation/validatePhone";
import { validateEmail } from "../validation/validateEmail";

import { createGTMEvent } from "../../../../utils/tracking/gtm";
import {
  LeadType,
  PageType,
} from "../../../../../__types__/generated/globalTypes";
import { DiggerType, useDiggerId } from "../../../../utils/hooks/useDigger";

import { range } from "../utils/range";
import moment from "moment";
import { client } from "../../../../../../client/__graphql__/client";
moment.locale("nb");

export interface IShowingItem {
  id?: string;
  label: string;
  unformatted: string;
  maxParticipants: number;
}

export interface ITimeItem {
  from: string;
  to: string;
  availableSlots: number;
  participants: number;
  firstWithNoParticipants: boolean;
}

export interface IParticipants {
  id: number;
  label: string;
  value: any;
}

interface IFormContext {
  networkError: boolean;
  statusCheck: boolean;
  interval: boolean;
  showForm: boolean;
  showCount: boolean;
  error: string;
  setError: (value: string) => void;
  posting: boolean;
  fetchingShowing: boolean;
  fetchingShowings: boolean;
  success: boolean;
  setSuccess: (value: boolean) => void;
  failed: string;
  showings: Array<IShowingItem>;
  selectedShowing: IShowingItem | undefined;
  setSelectedShowing: (item: IShowingItem) => void;
  available: Array<ITimeItem>;
  setSelectedTime: (value: string) => void;
  selectedTimeError: string;
  selectedTime: string;
  noAvailbleWithoutParticipants: boolean;
  contact: boolean;
  participantItems: Array<IParticipants>;
  participants: IParticipants | null;
  setParticipants: (value: IParticipants) => void;
  participantError: string;
  setParticipantError: (value: string) => void;
  firstName: string;
  setFirstName: (value: string) => void;
  firstNameError: string;
  setFirstNameError: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  lastNameError: string;
  setLastNameError: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  emailError: string;
  setEmailError: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  phoneError: string;
  setPhoneError: (value: string) => void;
  comment: string;
  setComment: (value: string) => void;
  charactersLeft: number;
  commentError: string;
  printed: boolean;
  showPrinted: boolean;
  setPrinted: (value: boolean) => void;
  printedConsentError: string;
  valuation: boolean;
  setValuation: (value: boolean) => void;
  zip: string;
  zipError: string;
  setZip: (value: string) => void;
  handleSubmit: (e: React.SyntheticEvent) => void;
  setSamtykkeIds: (value: any) => void;
  samtykkeIds: number[];
}

export const FormContext = React.createContext<IFormContext>({
  networkError: false,
  statusCheck: true,
  interval: true,
  showForm: false,
  showCount: true,
  error: "",
  setError: () => {},
  posting: false,
  fetchingShowing: false,
  fetchingShowings: false,
  success: false,
  setSuccess: () => {},
  failed: "",
  showings: [],
  selectedShowing: undefined,
  setSelectedShowing: () => {},
  available: [],
  noAvailbleWithoutParticipants: false,
  selectedTime: "",
  setSelectedTime: () => {},
  selectedTimeError: "",
  contact: false,
  participantItems: [],
  participants: null,
  setParticipants: () => {},
  participantError: "",
  setParticipantError: () => {},
  firstName: "",
  setFirstName: () => {},
  firstNameError: "",
  setFirstNameError: () => {},
  lastName: "",
  setLastName: () => {},
  lastNameError: "",
  setLastNameError: () => {},
  email: "",
  setEmail: () => {},
  emailError: "",
  setEmailError: () => {},
  phone: "",
  setPhone: () => {},
  phoneError: "",
  setPhoneError: () => {},
  comment: "",
  setComment: () => {},
  commentError: "",
  charactersLeft: 0,
  printed: false,
  showPrinted: false,
  setPrinted: () => {},
  printedConsentError: "",
  valuation: false,
  setValuation: () => {},
  zip: "",
  zipError: "",
  setZip: () => {},
  handleSubmit: () => {},
  setSamtykkeIds: () => {},
  samtykkeIds: [],
});

export const FormContextProvider: React.FC<{
  sguid: string;
  selectedIndex?: number;
  pmgraphClient: ApolloClient<NormalizedCacheObject>;
}> = React.memo(({ sguid, pmgraphClient, selectedIndex, children }) => {
  const { submitWithDigger } = useDiggerId();
  const [interval, setInterval] = React.useState(true);
  const [error, setError] = React.useState<string>("");
  const [networkError, setNetworkError] = React.useState<boolean>(false);
  const [showForm, setShowForm] = React.useState<boolean>(false);
  const [showCount, setShowCount] = React.useState<boolean>(true);

  const [showings, setShowings] = React.useState<Array<IShowingItem>>([]);
  const [showing, setShowing] = React.useState<IShowingItem>();

  const [available, setAvailable] = React.useState<Array<ITimeItem>>([]);
  const [noAvailbleWithoutParticipants, setNoAvailbleWithoutParticipants] =
    React.useState<boolean>(false);
  const [selectedTime, setSelectedTime] = React.useState<string>("");
  const [selectedTimeError, setSelectedTimeError] = React.useState<string>("");

  const [contact, setContact] = React.useState<boolean>(false);

  const [success, setSuccess] = React.useState<boolean>(false);
  const [failed, setFailed] = React.useState<string>("");

  const [participants, setParticipants] = React.useState<IParticipants | null>(
    null
  );
  const [participantItems, setParticipantItems] = React.useState<
    Array<IParticipants>
  >([]);
  const [participantError, setParticipantError] = React.useState<string>("");

  const [firstName, setFirstName] = React.useState<string>("");
  const [firstNameError, setFirstNameError] = React.useState<string>("");

  const [lastName, setLastName] = React.useState<string>("");
  const [lastNameError, setLastNameError] = React.useState<string>("");

  const [email, setEmail] = React.useState<string>("");
  const [emailError, setEmailError] = React.useState<string>("");

  const [phone, setPhone] = React.useState<string>("");
  const [phoneError, setPhoneError] = React.useState<string>("");

  const [comment, setComment] = React.useState<string>("");
  const [commentMaxLength] = React.useState<number>(150);
  const [charactersLeft, setCharactersLeft] = React.useState<number>(0);
  const [commentError, setCommentError] = React.useState<string>("");
  const [zip, setZip] = React.useState<string>("");
  const [zipError, setZipError] = React.useState<string>("");
  const [samtykkeIds, setSamtykkeIds] = React.useState<number[]>([]);

  /* Consents */
  const [showPrinted, setShowPrinted] = React.useState(false);
  const [printedConsentError, setPrintedConsentError] = React.useState("");
  const [printed, setPrinted] = React.useState(false);
  const [valuation, setValuation] = React.useState<boolean>(false);

  React.useEffect(() => {
    let commentLength = comment.length;
    setCharactersLeft(commentMaxLength - commentLength);
  }, [comment]);

  const [settings, { loading: statusCheck }] = useLazyQuery(
    GET_REGISTRATION_STATUS_QUERY,
    {
      onError: ({ networkError }) => {
        if (networkError?.message) {
          setNetworkError(true);
        }
      },
      onCompleted: ({ externalSettings }) => {
        if (externalSettings?.show) {
          setShowForm(true);
        } else {
          setShowForm(false);
        }
        if (externalSettings?.showCount) {
          setShowCount(true);
        } else {
          setShowCount(false);
        }
        if (
          typeof externalSettings?.products?.showBoligpresentasjon === "boolean"
        ) {
          setShowPrinted(externalSettings.products.showBoligpresentasjon);
        }
      },
    }
  );

  React.useEffect(() => {
    settings({
      variables: {
        input: {
          sguid: sguid,
        },
      },
    });
  }, []);

  const handleSetPrinted = (value: boolean) => {
    setPrinted(value);
    if (showing && value) {
      handleCheckShowing(showing);
    } else if (!value) {
      setPrintedConsentError("");
    }
  };

  const handleCheckShowing = (item: IShowingItem) => {
    if (item?.unformatted) {
      const date = moment(item?.unformatted);
      if (date.isValid()) {
        const currentDate = moment();
        const currentWeekday = currentDate.isoWeekday();
        const showingTomorrow = moment(date).diff(currentDate, "day") === 0;

        const deadlineOtherDays = moment()
          .set("hours", 15)
          .set("minutes", 0)
          .set("seconds", 0);
        const deadlineMonday = moment()
          .set("hours", 13)
          .set("minutes", 0)
          .set("seconds", 0);

        if (
          currentWeekday >= 2 &&
          currentWeekday <= 5 &&
          showingTomorrow &&
          currentDate.isAfter(deadlineOtherDays, "minutes")
        ) {
          setPrintedConsentError(
            "Bestillingen av trykket boligpresentasjon er for tett opp mot visning og kan derfor ikke leveres. Kontakt gjerne megler hvis spørsmål."
          );
        } else if (
          currentWeekday === 5 &&
          currentDate.isAfter(deadlineMonday, "minutes")
        ) {
          setPrintedConsentError(
            "Bestillingen av trykket boligpresentasjon er for tett opp mot visning og kan derfor ikke leveres. Kontakt gjerne megler hvis spørsmål."
          );
        } else {
          if (date.isSame(currentDate, "day")) {
            setPrintedConsentError(
              "Bestillingen av trykket boligpresentasjon er for tett opp mot visning og kan derfor ikke leveres. Kontakt gjerne megler hvis spørsmål."
            );
          } else {
            setPrintedConsentError("");
          }
        }
      }
    }
  };

  const handleSelectShowing = (item: IShowingItem) => {
    setShowing(item);
  };

  /* Check selected showing (Print consent) */

  /* Fetch showings for property */

  const [getViewings, { loading: fetchingShowings }] = useLazyQuery(
    GET_VIEWINGS_QUERY,
    {
      fetchPolicy: "network-only",
      onError: ({ networkError }) => {
        if (networkError?.message === "Failed to fetch") {
          setError(
            "Tjenesten er for øyeblikket ikke tilgjengelig. Vennligst forsøk igjen."
          );
        }
      },
      onCompleted: ({ externalGetAllViewings }) => {
        setShowings([]);
        for (let showing of externalGetAllViewings) {
          let showingItem: IShowingItem = {
            id: showing.identifier,
            unformatted: moment(
              `${showing.date} ${showing.from}:00`,
              "DD.MM.YYYY HH:mm:ss"
            ).format("YYYY-MM-DD HH:mm:ss"),
            label: `${showing.dateFormatted} ${showing.from} - ${showing.to}`,
            maxParticipants: showing.maxParticipants?.value || 0,
          };
          setShowings((prev: Array<IShowingItem>) => [...prev, showingItem]);
        }
        setShowings((prev: Array<IShowingItem>) => [
          ...prev,
          {
            id: "0",
            unformatted: "",
            label: "Kontakt megler for ønskelig tidspunkt",
            maxParticipants: 1,
          },
        ]);
      },
    }
  );

  React.useEffect(() => {
    if (showings.length === 0) {
      getViewings({
        variables: {
          input: {
            sguid: sguid,
          },
        },
      });
    }
  }, [, showings]);

  React.useEffect(() => {
    if (typeof selectedIndex !== "undefined") {
      const selected = showings[selectedIndex];
      handleSelectShowing(selected);
    }
  }, [showings]);

  // Fetch selected showing

  const [getViewing, { loading: fetchingShowing }] = useLazyQuery(
    GET_VIEWING_QUERY,
    {
      fetchPolicy: "network-only",
      onCompleted: ({ externalGetViewing }) => {
        setAvailable([]);
        if (!externalGetViewing?.interval) {
          setInterval(false);
        }
        if (externalGetViewing.available?.info?.items) {
          const items = externalGetViewing.available.info.items;
          for (let item of items) {
            setAvailable((prev: any) => [...prev, item]);
          }
          setParticipants({ id: 1, label: "1", value: 1 });
        }
        if (
          externalGetViewing.available?.info?.noTimesWithoutParticipants ===
          true
        ) {
          setNoAvailbleWithoutParticipants(true);
        }
      },
    }
  );

  React.useEffect(() => {
    if (showing?.id && showing?.id != "0") {
      getViewing({
        variables: {
          input: {
            identifier: showing?.id,
          },
        },
      });
    }
  }, [showing]);

  const createParticipantList = (count: number = 5) => {
    const items = range(count);
    setParticipantItems([]);
    for (let item of items) {
      setParticipantItems((prev: any) => [
        ...prev,
        { id: item + 1, label: item + 1, value: item + 1 },
      ]);
    }
  };

  // Create participant list by available slots for this particular time.

  React.useEffect(() => {
    if (available.length && selectedTime) {
      const item = available.filter((item) => item.from === selectedTime)[0];
      if (item?.availableSlots) {
        createParticipantList(item.availableSlots);
      }
    }
  }, [available, selectedTime]);

  React.useEffect(() => {
    setAvailable([]);
    setNoAvailbleWithoutParticipants(false);
    setSelectedTime("");
    if (!interval) {
      createParticipantList(5);
    } else if (showing?.id === "0") {
      createParticipantList(5);
      setContact(true);
    } else {
      setContact(false);
    }
  }, [showing, interval]);

  const [addReservation, { loading: posting }] = useMutation(
    RESERVATION_MUTATION,
    {
      onCompleted: ({ externalAddReservation }) => {
        if (externalAddReservation.success) {
          setSuccess(true);

          createGTMEvent({
            event: "pmFormSubmission",
            gaEvent: "RT_SHOWING_BOOKING",
            gaCategory: "RT_SHOWING_BOOKING",
            gaAction: "RT_SEND_SHOWING_BOOKING",
          });

          if (samtykkeIds.length) {
            registrerSamtykker({
              variables: {
                input: {
                  navn: `${firstName} ${lastName}`,
                  epost: `${email}`,
                  telefon: `${phone}`,
                  tjeneste: 1,
                  samtykker: samtykkeIds,
                },
              },
            });
          }

          if (valuation) {
            submitWithDigger(
              DiggerType.VERDIVURDERING,
              {
                zip: zip,
              },
              (diggerData) => {
                postValuation({
                  variables: {
                    input: {
                      leadType: LeadType.VERDIVURDERING,
                      pageType: PageType.OBJEKT,
                      identifier: sguid,
                      email: email,
                      phone: phone,
                      comment: comment,
                      referer: window.location.href,
                      zip: zip,
                      diggerId: diggerData?.diggerId,
                    },
                  },
                });
              }
            );
          }
        }
      },
      onError: ({ graphQLErrors }) => {
        if (graphQLErrors) {
          let firstError = graphQLErrors[0].message;
          setFailed(firstError);
        }
      },
    }
  );

  const [postValuation] = useMutation(VALUATION_MUTATION, {
    client: pmgraphClient,
    onCompleted: ({ lead }) => {
      if (lead.valuation.success) {
        createGTMEvent({
          event: "pmFormSubmission",
          gaEvent: "RT_SHOWING_BOOKING_VALUATION",
          gaCategory: "RT_VALUATION",
          gaAction: "RT_SEND_SHOWING_BOOKING",
        });
      }
    },
    onError: ({ graphQLErrors }) => {
      console.log(graphQLErrors);
    },
  });

  const [registrerSamtykker] = useMutation(SAMTYKKER_MUTATION, {
    client: client,
  });
  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();

    let errors = [];
    if (typeof showing === "undefined") {
      errors.push("time");
      setSelectedTimeError("Mangler tidspunkt");
    } else {
      setSelectedTimeError("");
    }
    if (!participants?.id) {
      errors.push("participants");
      setParticipantError("Mangler antall personer");
    } else {
      setParticipantError("");
    }
    if (!firstName) {
      errors.push("firstname");
      setFirstNameError("Mangler fornavn.");
    } else {
      setFirstNameError("");
    }
    if (!lastName) {
      errors.push("lastname");
      setLastNameError("Mangler etternavn.");
    } else {
      setLastNameError("");
    }
    if (!email) {
      errors.push("email");
      setEmailError("Mangler e-post.");
    } else if (!validateEmail(email)) {
      errors.push("email");
      setEmailError("E-post er ikke gyldig");
    } else {
      setEmailError("");
    }
    if (!phone) {
      errors.push("phone");
      setPhoneError("Mangler mobil.");
    } else if (!validatePhone(phone)) {
      errors.push("phone");
      setPhoneError("Mobil er ikke gyldig");
    } else {
      setPhoneError("");
    }

    if (charactersLeft < 0) {
      setCommentError(
        `Kommentar kan ikke bestå av flere enn ${commentMaxLength} tegn`
      );
      errors.push("comment");
    } else {
      setCommentError("");
    }

    if (!zip) {
      setZipError("Mangler postnummer.");
      errors.push("zip");
    } else if (zip.length !== 4 || isNaN(parseInt(zip))) {
      setZipError("Postnummer må være 4 tall.");
      errors.push("zip");
    } else if (zipError) {
      setZipError("");
    }

    if (errors.length) {
      return;
    }

    //TODO add consent
    addReservation({
      variables: {
        input: {
          identifier: showing?.id || null,
          sguid: sguid,
          contact,
          from: interval
            ? selectedTime
              ? selectedTime
              : null
            : moment(showing?.unformatted, "YYYY-MM-DD HH:mm:ss").format(
                "HH:mm"
              ),
          participants: participants?.value,
          firstName: firstName,
          lastName: lastName,
          email: email,
          phone: phone,
          comment: comment,
          zip: zip,
          source: "Visningspåmelding",
          printed: printed,
          valuation: valuation,
          consents: {
            finansieringmarkedsforing: samtykkeIds.includes(5),
            marketingPromo: samtykkeIds.includes(7),
          },
        },
      },
    });
  };

  const setIds = (id: number) => {
    setSamtykkeIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  return (
    <FormContext.Provider
      value={{
        networkError,
        statusCheck,
        interval,
        showForm,
        showCount,
        error,
        setError,
        posting,
        fetchingShowings,
        fetchingShowing,
        success,
        setSuccess,
        failed,
        showings,
        selectedShowing: showing,
        setSelectedShowing: handleSelectShowing,
        available,
        selectedTime,
        setSelectedTime,
        selectedTimeError,
        noAvailbleWithoutParticipants,
        contact,
        participantItems,
        participants,
        setParticipants,
        participantError,
        setParticipantError,
        firstName,
        setFirstName,
        firstNameError,
        setFirstNameError,
        lastName,
        setLastName,
        lastNameError,
        setLastNameError,
        email,
        setEmail,
        emailError,
        setEmailError,
        phone,
        setPhone,
        phoneError,
        setPhoneError,
        comment,
        setComment,
        commentError,
        charactersLeft,
        showPrinted,
        printed,
        setPrinted: handleSetPrinted,
        printedConsentError,
        valuation,
        setValuation,
        zip,
        setZip,
        zipError,
        handleSubmit,
        setSamtykkeIds: setIds,
        samtykkeIds,
      }}
    >
      {children}
    </FormContext.Provider>
  );
});
